
import * as Promise from 'bluebird';

let interceptor = [];
Promise.config({
  // Enable warnings
  warnings: false,
  // Enable long stack traces
  longStackTraces: true,
  // Enable cancellation
  cancellation: true,
  // Enable monitoring
  monitoring: false
});

export class Response {

  /**
   * Initialize the response object.
   * @param {Request} request - origin request object
   * @param {jQuery} ajax - jquery ajax function
   * @param {Object|null} callback
   */
  constructor(request, ajax, callback = null) {

    this.request = request;
    this._callback = callback;
    this.promise = new Promise((resolve, reject) => {
      this._resolve = resolve;
      this._reject = reject;
    });

    this.errorHandled = false;
    if (interceptor.length) {
      interceptor.forEach((cb) => cb(this));
    }

    this._ajax = ajax
      .fail(this.handleError.bind(this))
      .done(this.handleSuccess.bind(this));
  }

  /**
   * Returns the Promise class. To be used for changing global settings.
   * @return {Promise}
   * @constructor
   */
  static Promise() {
    return Promise;
  }

  static interceptError(cb) {
    if (typeof cb === 'function') {
      interceptor.push(cb);
    }
  }

  /**
   * Callback that handles the ajax success state
   * @param {mixed} data
   * @param {String} textStatus
   * @param {jqXHR} jqXHR - jquery wrapped xhr object
   */
  handleSuccess(data, textStatus, jqXHR) {
    this.xhr = jqXHR;
    this.data = data;
    this.status = this.xhr.status;
    this.textStatus = textStatus;

    this._resolve(this.data);
  }

  /**
   * Callback that handles error state
   * @param {jqXHR} jqXHR - jquery wrapped xhr object
   * @param textStatus
   * @param errorThrown
   */
  handleError(jqXHR, textStatus, errorThrown) {
    this.xhr = jqXHR;
    this.status = this.xhr.status;
    this.textStatus = textStatus;
    this.error = this._parseError(jqXHR);

    this._callback ? this._callback.callback(this._callback.app) : this._reject(this.error);
  }

  /**
   * Parse the error message that comes back from the ARGO API
   * to get a consistently structure.
   * @param {jqXHR} jqXHR - jqXHR object from the error handler
   * @return {Object}
   * @private
   */
  _parseError(jqXHR) {
    let errObj = {};
    try {
      errObj = JSON.parse(jqXHR.responseText);
    } catch (e) {
      //create a "fake" api response when the response is not parseable (e.g. comes from Cloudfront)
      errObj = {
        status: jqXHR.status,
        error: jqXHR.statusText,
        message: jqXHR.statusText,
        errCode: 'HTTP_STATUS_' + jqXHR.status,
        timestamp: new Date().toISOString(),
        path: jqXHR.responseURL,
        responseBody: jqXHR.responseText
      }
    }

    // keep original jquery text status
    errObj.textStatus = this.textStatus;

    if (!errObj.status) {
      errObj.status = jqXHR.status;
    }

    switch (errObj.errCode) {
      case 'INVALID_PROPERTY_VALUE':
      case 'REQUIRED_PROPERTY_VALUE':
      case 'UNIQUE_CONSTRAINT_VIOLATION':
      case 'FOREIGN_KEY_CONSTRAINT_VIOLATION':
        errObj.errorProperty = errObj.params[0].split(".").pop();
        errObj.errorMessageCode = errObj.errCode;

        if (errObj.params.length > 1) {
          errObj.errorPropertyValue = errObj.params[1];
        }
        break;
    }

    return errObj;
  }

  /**
   * Add a callback function to the promise chain that will be called
   * in success (resolved) or error (rejected) state.
   * @param {Function} cb - callback
   * @return {Response}
   */
  finally(cb) {
    this.promise = this.promise.finally(cb);
    return this;
  }

  /**
   * Add a callback function to the promise chain that will be called
   * in success (resolved) state.
   * @param {Function} cb - callback
   * @param {Function} cbError - error-callback
   * @return {Response}
   */
  then(cb, cbError) {
    if (!cbError) {
      this.promise = this.promise.then(cb);
      return this;
    }
    this.promise = this.promise.then(cb, cbError);
    return this;
  }

  /**
   * Add a callback function to the promise chain, that will be called
   * in error (rejected) state. The first argument `pred` is optional and
   * can be used to filter out the error type and handle only this certain
   * type of error.
   *
   * Example:
   *  .catch({status: 500}, ...)
   *  handles only HTTP 500 errors, all other errors will skip this callback
   *
   * @param {Object|Function|Error} [pred] - predicate the error object
   * @param {Function} cb - callback handler
   * @return {Response}
   */
  catch(pred, cb) {
    if (!cb) {
      this.promise = this.promise.catch(pred.bind(this.promise));
    }
    else {
      this.promise = this.promise.catch(pred, cb.bind(this.promise));
    }
    return this;
  }

  /**
   * Mark the promise chain as finished, that means an unhandled rejection
   * will throw an JavaScript error here.
   * @return {Response}
   */
  done() {
    this.promise = this.promise.done();
    return this;
  }
}